import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SystemInfo {
  id: string;
  name: string;
  code: string;
  is_available_in_plan: boolean;
}

interface SystemSelectorProps {
  companyId: string;
  selectedSystems: string[];
  onSystemsChange: (systemIds: string[]) => void;
  disabled?: boolean;
}

export function SystemSelector({
  companyId,
  selectedSystems,
  onSystemsChange,
  disabled = false
}: SystemSelectorProps) {
  const { data: availableSystems = [], isLoading } = useQuery({
    queryKey: ['available-systems-for-company', companyId],
    queryFn: async (): Promise<SystemInfo[]> => {
      if (!companyId) return [];
      
      // Get systems available in company plans
      const { data: companyPlans, error: plansError } = await supabase
        .from('company_system_plans')
        .select(`
          system_id,
          status,
          systems:system_id (
            id,
            name,
            code
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'active');

      if (plansError) throw plansError;

      return companyPlans?.map(plan => {
        const system = Array.isArray(plan.systems) ? plan.systems[0] : plan.systems;
        return {
          id: system?.id || '',
          name: system?.name || '',
          code: system?.code || '',
          is_available_in_plan: true
        };
      }) || [];
    },
    enabled: !!companyId,
  });

  const handleSystemToggle = (systemId: string, checked: boolean) => {
    if (disabled) return;
    
    const newSelectedSystems = checked 
      ? [...selectedSystems, systemId]
      : selectedSystems.filter(id => id !== systemId);
    
    onSystemsChange(newSelectedSystems);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Carregando sistemas...</span>
      </div>
    );
  }

  if (availableSystems.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <p>Nenhum sistema disponível nos planos ativos da empresa.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground">
        Selecione os sistemas que o usuário poderá acessar:
      </div>
      {availableSystems.map((system) => (
        <div key={system.id} className="flex items-center space-x-3 p-3 border rounded-lg">
          <Checkbox
            id={system.id}
            checked={selectedSystems.includes(system.id)}
            onCheckedChange={(checked) => 
              handleSystemToggle(system.id, checked as boolean)
            }
            disabled={disabled}
          />
          <div className="flex-1">
            <Label 
              htmlFor={system.id}
              className="text-sm font-medium cursor-pointer"
            >
              {system.name}
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">
                {system.code}
              </Badge>
              {system.is_available_in_plan && (
                <Badge variant="secondary" className="text-xs">
                  Incluído no plano
                </Badge>
              )}
            </div>
          </div>
        </div>
      ))}
      {selectedSystems.length > 0 && (
        <div className="text-sm text-muted-foreground">
          {selectedSystems.length} sistema{selectedSystems.length !== 1 ? 's' : ''} selecionado{selectedSystems.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}